import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';

import { authFetchWithExchangeRetry } from '../../lib/auth-fetch-with-exchange-retry.js';
import { reportConsultError } from '../../lib/report-consult-error.js';
import { formatUsPhoneMask, normalizeUsPhoneDigits } from '../../utils/us-phone-input-mask.js';
import { notifyParent } from '../embed/embedded.js';
import type { ConsultMemberDetails } from '../exchange/exchange-types.js';
import type { GroupConsultEvent } from '../group-consults/group-consult-calendar.js';
import { formatEventTimeRangeCompact } from '../group-consults/group-consult-calendar.js';

type RegisterWebinarResponse = {
  joinUrl: string;
  registrantId?: string;
  webinarId?: string;
  startTime?: string;
  topic?: string;
  /** Local dev mock: Zoom API skipped; same confirmation UX as a live registration. */
  mockRegistration?: boolean;
};

/** After successful webinar registration (mock or Zoom); dismissed with Close. */
export type RegistrationConfirmation = {
  title: string;
  dateTimeDisplay: string;
  email: string;
  /** Zoom registrant join URL when registration hit the Zoom API (open when the session starts). */
  joinUrl?: string;
};

export type WebinarRegistration = {
  /** Id of the webinar whose registration modal is open, or `null` when closed. */
  registeringWebinarId: string | null;
  /** Display value for masked US phone `(555) 123-4567` in the registration modal. */
  registerPhoneDisplay: string;
  setRegisterPhoneDisplay: (display: string) => void;
  registerError: string | null;
  registerLoading: boolean;
  registrationConfirmation: RegistrationConfirmation | null;
  handleRegisterClick: (event: GroupConsultEvent) => void;
  handleRegisterFormSubmit: (e: FormEvent<HTMLFormElement>, webinarId: string) => void;
  closeRegisterModal: () => void;
  dismissRegistrationConfirmation: () => void;
};

/**
 * Owns webinar registration: which session's modal is open, the phone mask, the POST, and the
 * confirmation that replaces the modal.
 *
 * @remarks Lifted verbatim out of the vendor page. `memberDetails` both pre-fills AND locks the
 * name/email fields, so a member-backed link submits the exchange's identity, never the form's.
 */
export function useWebinarRegistration(params: {
  jwt: string | null;
  setJwt: (jwt: string) => void;
  token: string | undefined;
  apiBase: string;
  events: GroupConsultEvent[];
  displayTimeZone: string;
  memberDetails: ConsultMemberDetails | null;
}): WebinarRegistration {
  const { jwt, setJwt, token, apiBase, events, displayTimeZone, memberDetails } = params;
  const [registeringWebinarId, setRegisteringWebinarId] = useState<string | null>(null);
  const [registerPhoneDisplay, setRegisterPhoneDisplay] = useState('');
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registrationConfirmation, setRegistrationConfirmation] =
    useState<RegistrationConfirmation | null>(null);

  const registerForWebinar = async (
    webinarId: string,
    details: { email: string; firstName: string; lastName: string; phone: string },
  ) => {
    if (!jwt) return;
    setRegisterError(null);
    setRegisterLoading(true);
    try {
      const res = await authFetchWithExchangeRetry({
        magicLinkToken: token,
        setJwt,
        apiBase,
        jwt,
        url: `${apiBase}/consult-scheduling/webinars/${webinarId}/register`,
        init: {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: details.email,
            firstName: details.firstName,
            lastName: details.lastName,
            phone: details.phone,
          }),
        },
      });
      if (!res.ok) {
        const errBody: unknown = await res.json().catch(() => ({}));
        throw new Error(readErrorMessage(errBody) ?? 'Registration failed');
      }
      const data = (await res.json()) as RegisterWebinarResponse;
      const joinUrl =
        typeof data.joinUrl === 'string' && data.joinUrl.trim().length > 0
          ? data.joinUrl.trim()
          : undefined;
      if (data.mockRegistration || joinUrl) {
        const ev = events.find((e) => e.id === webinarId);
        const titleFromApi = typeof data.topic === 'string' ? data.topic.trim() : '';
        setRegistrationConfirmation({
          title: ev?.title ?? (titleFromApi || 'Group consult'),
          dateTimeDisplay: ev ? formatEventTimeRangeCompact(ev.start, ev.end, displayTimeZone) : '',
          email: details.email,
          ...(joinUrl ? { joinUrl } : {}),
        });
        setRegisteringWebinarId(null);
        setRegisterPhoneDisplay('');
        notifyParent({ type: 'nrx-consult:booked', consultType: 'group' });
        return;
      }
      throw new Error('No join URL returned');
    } catch (err) {
      // This block also wraps `setRegisterLoading`, so an unexpected throw anywhere inside it is
      // reported to the MEMBER as a registration failure specifically. Without a report, an
      // operator has no way to tell that apart from Zoom actually rejecting the registrant.
      reportConsultError('webinar-registration', err);
      setRegisterError(
        err instanceof Error ? err.message : 'Unable to register. Please try again.',
      );
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleRegisterClick = (event: GroupConsultEvent) => {
    setRegisterError(null);
    setRegisteringWebinarId(event.id);
  };

  const handleRegisterFormSubmit = (e: FormEvent<HTMLFormElement>, webinarId: string) => {
    e.preventDefault();
    const form = e.currentTarget;
    const phoneDigits = normalizeUsPhoneDigits(registerPhoneDisplay);
    let details: { email: string; firstName: string; lastName: string; phone: string };
    if (memberDetails) {
      details = { ...memberDetails, phone: phoneDigits };
    } else {
      details = {
        email: readFieldValue(form, 'register-email'),
        firstName: readFieldValue(form, 'register-firstName'),
        lastName: readFieldValue(form, 'register-lastName'),
        phone: phoneDigits,
      };
    }
    if (details.email && details.firstName && details.lastName && phoneDigits.length === 10) {
      void registerForWebinar(webinarId, details);
    } else {
      setRegisterError(
        'Please fill in email, first name, last name, and a complete 10-digit US phone number.',
      );
    }
  };

  const closeRegisterModal = () => {
    setRegisteringWebinarId(null);
    setRegisterError(null);
    setRegisterPhoneDisplay('');
  };

  useEffect(() => {
    if (!registeringWebinarId) return;
    const seed = memberDetails?.phone ?? '';
    setRegisterPhoneDisplay(formatUsPhoneMask(seed));
  }, [registeringWebinarId, memberDetails?.phone]);

  return {
    registeringWebinarId,
    registerPhoneDisplay,
    setRegisterPhoneDisplay,
    registerError,
    registerLoading,
    registrationConfirmation,
    handleRegisterClick,
    handleRegisterFormSubmit,
    closeRegisterModal,
    dismissRegistrationConfirmation: () => setRegistrationConfirmation(null),
  };
}

/**
 * Reads a named field's trimmed value from the registration form.
 *
 * @remarks Replaces the vendor's `(form.elements.namedItem(name) as HTMLInputElement)?.value?.trim()`
 * — same result (`''` when the field is absent), without an unchecked cast.
 */
function readFieldValue(form: HTMLFormElement, name: string): string {
  const element = form.elements.namedItem(name);
  return element instanceof HTMLInputElement ? element.value.trim() : '';
}

/** Pulls `message` off an error response body when the API sent one. */
function readErrorMessage(body: unknown): string | undefined {
  if (
    typeof body === 'object' &&
    body !== null &&
    'message' in body &&
    typeof body.message === 'string'
  ) {
    return body.message;
  }
  return undefined;
}
